/**
 * Déverrouillage d'une période clôturée : administrateur uniquement, motif obligatoire, toujours journalisé
 * (action « deverrouillage » avec la période et le motif).
 */
import { openFormModal } from '@presentation/shell/openFormModal';
import { esc } from '@presentation/shell/format';
import { fmtPeriode } from '@presentation/shell/format';
import { STYLE_AIDE, erreurChampHtml, majErreurChamp, type Notifier } from './panelKit';

export const MOTIF_MIN = 5;

export interface DeverrouillageDeps {
  estAdmin: boolean;
  periode: string;
  /** Vrai si décembre est déverrouillé alors que l'exercice est enregistré comme clôturé. */
  exerciceCloture: boolean;
  deverrouiller(motif: string): Promise<void>;
  notify: Notifier;
  apres?(): Promise<void> | void;
}

const P = 'deverrouillage';

export function ouvrirDeverrouillage(parent: HTMLElement, deps: DeverrouillageDeps): void {
  openFormModal(parent, { title: 'Déverrouiller la période', size: 'sm' }, (host, fermer) => {
    const mention = deps.exerciceCloture
      ? `<p style="${STYLE_AIDE}"><strong>Cette période appartient à un exercice déjà clôturé.</strong> Ne la rouvrez que pour une correction justifiée.</p>`
      : '';
    host.innerHTML = `
      <form id="${P}-form" novalidate>
        <p style="${STYLE_AIDE}">Les écritures de <strong>${esc(fmtPeriode(deps.periode))}</strong> pourront à nouveau être modifiées. L’opération est enregistrée dans le journal avec votre motif.</p>
        ${mention}
        <div class="field">
          <label for="${P}-motif">Motif du déverrouillage (obligatoire)</label>
          <input id="${P}-motif" type="text" autocomplete="off" required aria-required="true" aria-describedby="${P}-err">
          ${erreurChampHtml(`${P}-err`, undefined)}
        </div>
        <div style="display:flex;gap:10px;justify-content:flex-end;margin-top:14px;">
          <button type="button" class="btn btn-ghost" id="${P}-annuler">Annuler</button>
          <button type="submit" class="btn btn-danger" id="${P}-valider" ${deps.estAdmin ? '' : 'disabled'}>Déverrouiller</button>
        </div>
        ${deps.estAdmin ? '' : `<p style="${STYLE_AIDE}margin-top:8px;">Seul l’administrateur peut déverrouiller une période clôturée.</p>`}
      </form>`;
    const champ = host.querySelector<HTMLInputElement>(`#${P}-motif`);
    host.querySelector(`#${P}-annuler`)?.addEventListener('click', fermer);
    host.querySelector<HTMLFormElement>(`#${P}-form`)?.addEventListener('submit', (e) => {
      e.preventDefault();
      void (async () => {
        if (!deps.estAdmin) return;
        const motif = (champ?.value ?? '').trim();
        if (motif.length < MOTIF_MIN) {
          majErreurChamp(host, champ, `${P}-err`, `Indiquez le motif du déverrouillage (au moins ${MOTIF_MIN} caractères).`);
          champ?.focus();
          return;
        }
        majErreurChamp(host, champ, `${P}-err`, undefined);
        try {
          await deps.deverrouiller(motif);
        } catch (err) {
          majErreurChamp(host, champ, `${P}-err`, err instanceof Error ? err.message : 'Le déverrouillage a échoué.');
          return;
        }
        deps.notify.success(`Période ${fmtPeriode(deps.periode)} déverrouillée`);
        fermer();
        await deps.apres?.();
      })();
    });
    champ?.focus();
  });
}
